// The sea for the 3D look: an animated water surface over the west edge of the map (the sea
// tiles, see isSeaTile) and out from it as a bay, as wide as the map and SEA_REACH tiles deep:
// the open water the enemy's ships come in across. Land closes it in on its other three sides
// (scene3d builds those banks), and the map's other three sides are land too.
// One flat mesh with a small shader: rolling swell lit by the sun, a lighter colour
// near the coast fading to deep water further out, and foam breaking along the shore.
import * as THREE from 'three';
import { MAP_SIZE, SEA_REACH, SEA_WIDTH } from '@thrash/sim';

/** How far the water runs out from the map edge to the far shore, in tiles. */
export const SEA_OUT = SEA_REACH;
/** Just above the ground plane, so the water covers the sea tiles under it. */
const LEVEL = 0.01;

const vertex = /* glsl */ `
  attribute vec2 aTile;
  varying vec2 vTile;
  void main() {
    vTile = aTile;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform float uAngle;
  uniform vec3 uSun;
  uniform vec3 uView;
  uniform vec3 uNear;
  uniform vec3 uFar;
  uniform vec3 uSky;
  uniform float uShore;
  uniform float uFarShore;
  uniform float uMap;
  varying vec2 vTile;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
  }
  // swell height in tile space: a few travelling waves plus drifting noise
  float height(vec2 p) {
    float t = uTime;
    float h = 0.0;
    h += 0.50 * sin(dot(p, vec2(0.90, 0.40)) * 1.3 + t * 1.10);
    h += 0.35 * sin(dot(p, vec2(-0.50, 0.85)) * 1.9 + t * 1.45);
    h += 0.20 * sin(dot(p, vec2(0.20, -1.00)) * 3.1 + t * 2.10);
    h += 0.45 * noise(p * 1.7 + vec2(t * 0.35, t * 0.22));
    h += 0.25 * noise(p * 4.3 - vec2(t * 0.50, -t * 0.30));
    return h;
  }

  void main() {
    vec2 p = vTile;
    // distance out from the coastline, in tiles: 0 at the shore, growing out to sea
    float d = max(uShore - p.x, 0.0);
    // and from the bay's other shores, the far one and the two ends: shallow by every shore, deepest out in the middle
    float dFar = max(p.x - uFarShore, 0.0);
    float dEnds = min(max(p.y, 0.0), max(uMap - p.y, 0.0));
    float dEdge = min(min(d, dFar), dEnds);

    // normal from the height field, in tile space, then turned with the view
    float e = 0.05;
    float h = height(p);
    float hx = height(p + vec2(e, 0.0)) - h;
    float hy = height(p + vec2(0.0, e)) - h;
    vec2 g = vec2(hx, hy) / e * 0.16;
    float c = cos(uAngle), s = sin(uAngle);
    vec3 n = normalize(vec3(-(g.x * c - g.y * s), 1.0, -(g.x * s + g.y * c)));

    vec3 base = mix(uNear, uFar, smoothstep(0.0, 6.0, dEdge));
    float diffuse = 0.45 + 0.65 * max(dot(n, uSun), 0.0);
    vec3 col = base * diffuse;
    // crests a touch lighter and greener, troughs darker: reads as swell from above
    col *= 0.88 + 0.16 * smoothstep(-0.6, 1.4, h);
    // sky reflected more at grazing angles, sun glints on the crests
    float fres = pow(1.0 - max(dot(n, uView), 0.0), 3.0);
    col = mix(col, uSky, 0.35 * fres + 0.08);
    vec3 hv = normalize(uSun + uView);
    col += vec3(1.0, 0.95, 0.85) * pow(max(dot(n, hv), 0.0), 90.0) * 0.6;

    // foam: a ragged band breaking on the shore, and lines of it rolling in behind
    float rag = noise(p * 5.0 + uTime * 0.6);
    float edge = dEdge + 0.25 * rag;
    float shore = smoothstep(0.55, 0.05, edge);
    float roll = fract(d * 0.9 - uTime * 0.18);           // bands moving towards the shore
    float lines = smoothstep(0.0, 0.06, roll) * smoothstep(0.16, 0.06, roll);
    lines *= smoothstep(0.45, 0.75, noise(p * 2.2 + vec2(uTime * 0.1, 0.0))) * (1.0 - smoothstep(0.6, 3.5, d));
    col = mix(col, vec3(0.86, 0.90, 0.90), clamp(shore * (0.75 + 0.25 * rag) + lines * 0.55, 0.0, 1.0));

    gl_FragColor = vec4(col, 1.0);
    #include <colorspace_fragment>
  }
`;

export class Sea3D {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor(sunDir: THREE.Vector3, viewDir: THREE.Vector3) {
    this.material = new THREE.ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uAngle: { value: 0 },
        uSun: { value: sunDir.clone().normalize() },
        uView: { value: viewDir.clone().normalize() },
        uNear: { value: new THREE.Color(0x2c5a6e) },
        uFar: { value: new THREE.Color(0x0d1e28) },
        uSky: { value: new THREE.Color(0x9fc4d8) },
        uShore: { value: SEA_WIDTH },
        uFarShore: { value: -SEA_OUT },
        uMap: { value: MAP_SIZE },
      },
    });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.mesh.frustumCulled = false;
  }

  /**
   * Rebuilds the water for the current view angle. `toScene` turns a tile-space point
   * into scene units (the scene's ground()), so the water lines up with the land.
   */
  rebuild(toScene: (tx: number, ty: number, h?: number) => [number, number, number], angle: number): void {
    const N = MAP_SIZE, a = SEA_WIDTH;
    // one bay to the west, from the shore out to the far bank, exactly as wide as the map
    const strips: [number, number, number, number][] = [[-SEA_OUT, 0, a, N]];
    const pos: number[] = [], tile: number[] = [], index: number[] = [];
    for (const [x0, y0, x1, y1] of strips) {
      const k = pos.length / 3;
      for (const [tx, ty] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as const) {
        pos.push(...toScene(tx, ty, LEVEL));
        tile.push(tx, ty);
      }
      index.push(k, k + 2, k + 1, k, k + 3, k + 2);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('aTile', new THREE.Float32BufferAttribute(tile, 2));
    geo.setIndex(index);
    // the shader makes its own normals; these are for the scene's ambient-occlusion pass
    geo.computeVertexNormals();
    this.mesh.geometry.dispose();
    this.mesh.geometry = geo;
    this.material.uniforms.uAngle!.value = angle;
  }

  /** Moves the waves on. `now` is milliseconds, as performance.now() gives. */
  update(now: number): void {
    this.material.uniforms.uTime!.value = (now / 1000) % 10000;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
